import { useMemo } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Layout } from 'react-grid-layout'
import { useTranslation } from 'react-i18next'

import { Box } from '@enechain/ecloud-designsystem'

import { CardGridLayout, GridCard } from '~/components/common/CardGridLayout'
import { useAreaWeatherActual } from '~/hooks/weather/useAreaWeatherActual'
import { useAreaWeatherIcons } from '~/hooks/weather/useAreaWeatherIcons'
import { toProtoDate } from '~/models/dateTime'
import { buildKomaWeatherActual } from '~/models/weather/actualKoma'
import { AreaCodeChart } from '~/pages/BalanceDemand/components/RegionChart'
import type { SeriesVisibility } from '~/pages/BalanceDemand/constants'
import type { AreaCodeApiData } from '~/pages/BalanceDemand/hooks/useDemandDashboard'

type Props = {
  visibleAreaCodes: Set<AreaCode>
  /**
   * エリアセレクターの選択肢 (= 対象エリア ∩ 業務エリア設定、呼び出し元
   * `BalanceDemandPage` の `useFilterState` から渡す `filter.selectableAreaCodes`)。
   * AC-BDB-014「業務エリアが未設定」を、design 由来の「選択が空」空状態
   * (`visibleAreaCodes.size === 0`) と区別するために使う — 業務エリア設定が
   * 空の時だけこの配列自体が空になる (取得中・エラー時は呼び出し元が全 10
   * エリアへフォールバックするため空にならない)。
   */
  selectableAreaCodes: readonly AreaCode[]
  seriesVisibility: SeriesVisibility
  areaCodes: AreaCodeApiData[]
  displayDate: Date
  gcKoma: number
  currentKoma: number
  /** workflow-back-bar-readonly AC-13: 詳細リンクへ引き継ぐ caseId (undefined = 引き継がない)。 */
  caseId?: string
  /** `useGridCardLayout` (呼び出し元 = `BalanceDemandPage`) が計算したカード配置。 */
  layout: Layout
  /** ドラッグ / リサイズ確定時にそのまま渡すコールバック (`useGridCardLayout.onLayoutSettled`)。 */
  onLayoutSettled: (layout: Layout) => void
}

type CardProps = {
  item: AreaCodeApiData
  seriesVisibility: SeriesVisibility
  displayDate: Date
  gcKoma: number
  currentKoma: number
  caseId?: string
}

/**
 * カードの永続化 key (= `useGridCardLayout` の `cardIds` / `layout[].i`)。TS の
 * 数値 enum は逆引き `AreaCode[value]` で wire 値 (数値) と独立な名前文字列
 * (`"TOKYO"` 等) が取れるため、それを安定 key として使う。design
 * (`js/bg-data.js` の `gs-id=エリア名`) とはキーの中身こそ違う (向こうは表示用の
 * 日本語エリア名、こちらは enum 名) が、「フィルタで可視集合が変わっても
 * 安定 ID で保存位置を引く」という設計意図は同じ。`String(item.areaCode)`
 * (= wire 値の数値文字列) は使わない — 将来 enum の wire 値が振り直されると
 * 無関係なエリアへ既存レイアウトが対応してしまうため。`BalanceDemandPage` も
 * `cardIds` 算出にこの関数を使う (= 同じ id 生成ロジックを 2 箇所に書かない)。
 */
export function areaCodeStorageKey(areaCode: AreaCode): string {
  return AreaCode[areaCode]
}

/**
 * フィルタ絞り込み後の表示対象エリア一覧 (= 初期表示順序 AC-BDB-012 に従う
 * `areaCodes` の並び順をそのまま保つ)。`BalanceDemandPage` (cardIds 算出) と
 * `AreaCodeGrid` (実描画) の双方がこの 1 関数を通すことで、絞り込みロジックが
 * 2 箇所で独立に古くなることを防ぐ。
 */
export function selectVisibleAreaCodes(
  areaCodes: AreaCodeApiData[],
  visibleAreaCodes: Set<AreaCode>,
): AreaCodeApiData[] {
  return areaCodes.filter((r) => visibleAreaCodes.has(r.areaCode))
}

/**
 * エリアカード 1 枚。天気アイコン帯は dashboard 応答ではなく気象 gRPC
 * (WeatherForecastService) のライブ予報から組み立てる (= 気象 gRPC 1 本を
 * 全画面で使い回す設計)。エリアごとに hook を呼ぶためカード単位の
 * component に分離している (React hooks 規約)。
 */
const AreaCodeCard = ({
  item,
  seriesVisibility,
  displayDate,
  gcKoma,
  currentKoma,
  caseId,
}: CardProps): React.ReactElement => {
  const { weatherIcons, weatherTempValues } = useAreaWeatherIcons({
    areaCode: item.areaCode,
    displayDate,
    // 天気帯 (予報アイコン) も気温/予想気温 (ツールチップの予想気温。AC-BDB-010b)
    // も OFF の間は気象 gRPC (裏で BQ 最大 8 view) を発行しない。気温は天気アイコンと
    // 独立に扱うため、どちらか ON なら取得する。分析補助の親連動は呼び出し元が
    // 畳み込み済み (chartSeriesVisibility)。
    enabled: seriesVisibility.weather || seriesVisibility.temperature,
  })

  // ピルバッジに添える気温・実況天気は実績気象 gRPC (WeatherActualService) から
  // 合成する (#4494 の詳細画面と同じ経路)。天気帯 (予報) ではなく実績を引くのは、ピークが
  // 必ず到来済みコマに立つため — その時刻に「実際どうだったか」を出す。
  // 天気帯トグルに連動させない: ピルバッジは design bg.html で常時表示であり、
  // トグル OFF でピークの気温だけ消えるのは表示状態と業務値の混線になる。
  // 天気帯の過去コマも同じ実績を使う (AC-BDB-002 過去=実績アイコン)。
  const targetDate = toProtoDate(displayDate)
  const { data: weatherActualData } = useAreaWeatherActual({
    areaCode: item.areaCode,
    targetDateFrom: targetDate,
    targetDateTo: targetDate,
    // ピークが無い日 (未来日 / 取込前) はピル行ごと描かないので、天気帯 OFF なら
    // 気象も引かない。
    enabled: item.summary !== null || seriesVisibility.weather,
  })
  const weatherActual = useMemo(
    () => buildKomaWeatherActual(weatherActualData?.actual?.points ?? []),
    [weatherActualData],
  )

  return (
    <AreaCodeChart
      areaCode={item.areaCode}
      caseId={caseId}
      currentKoma={currentKoma}
      displayDate={displayDate}
      gcKoma={gcKoma}
      komaData={item.komaData}
      seriesVisibility={seriesVisibility}
      summary={item.summary}
      temperatureForecasts={weatherTempValues}
      weatherActual={weatherActual}
      weatherIcons={weatherIcons}
    />
  )
}

export const AreaCodeGrid = ({
  visibleAreaCodes,
  selectableAreaCodes,
  seriesVisibility,
  areaCodes,
  displayDate,
  gcKoma,
  currentKoma,
  caseId,
  layout,
  onLayoutSettled,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const visibleList = selectVisibleAreaCodes(areaCodes, visibleAreaCodes)

  // エリアのチェックを全部外した時 (業務エリアはあるが選択肢を自分で外した場合)。
  // 何も描かないとフィルタカードの下が完全な白紙になり、 **読み込み中なのか
  // 選択が空なのか判別できない**。 design `js/bg-data.js:265-268` が同じ位置で
  // `.bg-empty` を出す (`css/balance.css:418`: 中央寄せ・mid-emphasis・padding
  // 40px 20px)。 業務エリア設定自体が空のケース (AC-BDB-014) は下記
  // `selectableAreaCodes.length === 0` で別文言として優先判定する (design には
  // この 2 ケースの書き分けは無いため、design 由来の文言はこちらにのみ残す)。
  //
  // **判定は `visibleAreaCodes` (選択状態) を直接見る。** backend
  // (`balance/demand.go`) は D2 により選択・データの有無を問わず常に
  // 全 10 エリアを返すため、`visibleList` (= `areaCodes` を選択で絞ったもの) が
  // 0 件になるのは選択自体が空の時だけであり、「対象日にデータが無い」ケースの
  // 出し分けはもう発生しない (2026-09-14、backend 契約変更により到達不能化)。
  //
  // AC-BDB-014: 業務エリア設定そのものが空 (= 選択肢が 0 件) の場合は、design
  // 由来の「選択が空」文言 (`noAreaSelected`、業務エリアはあるがユーザーが
  // チェックを外した場合の文言) より優先して spec 指定の文言を出す。
  // `selectableAreaCodes` は業務エリア未取得・エラー時は全 10 エリアへ
  // フォールバック済みの値が渡るため、ここが 0 件になるのは業務エリア設定が
  // 本当に空の時だけ。
  const emptyMessage =
    selectableAreaCodes.length === 0
      ? t('page:balanceDemand.grid.businessAreasNotSet')
      : visibleAreaCodes.size === 0
        ? t('page:balanceDemand.grid.noAreaSelected')
        : null

  if (emptyMessage !== null) {
    return (
      <Box
        padding="40px 20px"
        color="Text.midEmphasis"
        fontSize="md"
        textAlign="center"
      >
        {emptyMessage}
      </Box>
    )
  }

  return (
    <CardGridLayout layout={layout} onLayoutSettled={onLayoutSettled}>
      {visibleList.map((item) => (
        <GridCard key={areaCodeStorageKey(item.areaCode)}>
          <AreaCodeCard
            caseId={caseId}
            currentKoma={currentKoma}
            displayDate={displayDate}
            gcKoma={gcKoma}
            item={item}
            seriesVisibility={seriesVisibility}
          />
        </GridCard>
      ))}
    </CardGridLayout>
  )
}
